import { useState } from 'react';
import { Divider, Space } from 'antd';
import { useFormik, FormikProvider } from 'formik';
import useCustomers from 'hooks/useCustomers';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import Notification from 'components/ui/base/notification/Notification';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { usePartyContext } from '../../PartyProvider';
import PartyDetails from './sections/party-details/PartyDetails';
import ContactDetails from './sections/contact-details/ContactDetails';
import SpecialInstructions from './sections/special-instructions/SpecialInstructions';
import { getOverviewValidationSchema } from './validationSchema';
import { mapCustomerToFormValues, mapFormValuesToParty } from './Utils';
import DutyAndVat from './sections/duty-and-vat/DutyAndVat';
import Billing from './sections/billing/Billing';
import { FormValues } from './types';
import useAep5DocumentModal from 'views/customers/hooks/useAep5DocumentModal';

const DividerWithHorizontalMargin = () => {
    return (
        <div style={{ marginInline: 24 }}>
            <Divider style={{ marginBlock: 2 }} />
        </div>
    );
};

const OverviewTab = () => {
    const { editCustomer } = useCustomers();
    const { customer, customerId, setCustomer, refreshCustomer } = usePartyContext();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const [isEditing, setIsEditing] = useState(false);
    // Reload the party so the AEP5 document status and the configuration status catch up
    const { checkShowAep5Modal, aep5Modal } = useAep5DocumentModal({ onUploaded: () => refreshCustomer() });

    const formik = useFormik<FormValues>({
        initialValues: mapCustomerToFormValues(customer),
        validationSchema: getOverviewValidationSchema(),
        validateOnChange: false,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                showGlobalOverlay({
                    type: 'LoadingOverlay',
                });
                const updatedParty = await editCustomer(customerId, mapFormValuesToParty(values, customer));

                if (updatedParty) {
                    setCustomer(updatedParty);
                    checkShowAep5Modal(updatedParty);
                }

                setIsEditing(false);

                Notification({
                    type: 'success',
                    messageTitle: 'Party Updated',
                    description: 'Successfully updated party data.',
                });
            } catch (error) {
                const errorMessage = getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE;
                Notification({
                    type: 'error',
                    messageTitle: 'Update Failed',
                    description: errorMessage,
                });
            } finally {
                hideGlobalOverlay();
            }
        },
    });

    return (
        <FormikProvider value={formik}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <div
                    style={{
                        width: '100%',
                        background: 'var(--gray-1)',
                        borderRadius: 6,
                        border: '1px solid var(--gray-4)',
                    }}
                >
                    <PartyDetails
                        isEditing={isEditing}
                        onEditClick={() => setIsEditing(true)}
                        onConfirm={() => (formik.dirty ? formik.submitForm() : setIsEditing(false))}
                        onCancel={() => {
                            formik.resetForm();
                            setIsEditing(false);
                        }}
                    />
                    <DividerWithHorizontalMargin />
                    <ContactDetails isEditing={isEditing} />
                    <DividerWithHorizontalMargin />
                    <DutyAndVat isEditing={isEditing} />
                    <DividerWithHorizontalMargin />
                    <Billing isEditing={isEditing} />
                    <DividerWithHorizontalMargin />
                    <SpecialInstructions isEditing={isEditing} />
                </div>
            </Space>

            {aep5Modal}
        </FormikProvider>
    );
};

export default OverviewTab;
